import * as React from "react";
import ModalPopup from "./modal/ModalPopup";
import ToggleButton from "./buttons/ToggleButton";
import SearchFieldSelect from "./SearchFieldSelect";
import CriteriaGroup from "../model/treatment/CriteriaGroup";
import Treatment from "../model/treatment/Treatment";
import TreatmentAssignment from "../model/treatment/TreatmentAssignment";
import AsyncAction from "../../AsyncAction";
import sfetch from "../utils/super-fetch";
import Option from "../model/Option";
import AssignedTreatmentItem from "./AssignedTreatmentItem";
import "./AssignmentEditor.scss";
import { byPosition } from "../utils/treatment";

declare var verticalDropdownUtil: any;

interface AssignmentEditorProps {
  assignments: TreatmentAssignment[];
  treatments: Treatment[];
  closeModal: () => any;
  onChange: (assignments: TreatmentAssignment[]) => any;
}

interface AssignmentEditorState {
  assignments: TreatmentAssignment[];
  displayLastTreatmentMessage?: boolean;
  applyIfHealthy: boolean;
  currentDraggedIndex?: number;
  currentDragOverIndex?: number;
}

export default class AssignmentEditor extends React.Component<
  AssignmentEditorProps,
  AssignmentEditorState
> {
  private criteriaGroup: CriteriaGroup;

  constructor(props: AssignmentEditorProps) {
    super(props);
    this.state = {
      assignments: this.props.assignments,
      applyIfHealthy: this.props.assignments[0] ? this.props.assignments[0].applyIfHealthy : false,
    };
    this.getUnassignedTreatmentOptions = this.getUnassignedTreatmentOptions.bind(this);
    this.assignTreatment = this.assignTreatment.bind(this);
    this.unassignTreatment = this.unassignTreatment.bind(this);
    this.toggleAutoAssign = this.toggleAutoAssign.bind(this);
    this.save = this.save.bind(this);
    this.criteriaGroup = this.state.assignments[0].criteriaGroup;
  }

  render() {
    return (
      <div className="assignment-editor">
        <ModalPopup closeModal={this.props.closeModal}>
          <div className="title">{this.criteriaGroup.i18n}</div>
          {this.state.displayLastTreatmentMessage && (
            <div className="message danger">
              {i18n["Form.EditAssignment.Alert.AssignmentMandatory"]}
            </div>
          )}
          <div>{i18n["Form.EditAssignment.Info"]}</div>
          <div className="react-form">
            <div className="vertical-container">
              {this.state.assignments.sort(byPosition).map((assignment, index) => (
                <React.Fragment key={assignment.treatment.uid}>
                  {this.state.currentDragOverIndex === index &&
                    this.state.currentDraggedIndex >= 0 &&
                    this.state.currentDraggedIndex !== index &&
                    this.state.currentDraggedIndex !== index - 1 && (
                      <div className="dropzone"></div>
                    )}
                  <div
                    className={this.state.currentDraggedIndex === index ? "dragged" : ""}
                    draggable={true}
                    onDragStart={(_) => this.onDragStart(index)}
                    onDragEnter={(_) => this.onDragEnter(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnd={(e) => this.onDrop(e, assignment)}
                  >
                    <AssignedTreatmentItem
                      key={assignment.treatment.uid}
                      assignment={assignment}
                      onDelete={this.unassignTreatment}
                      onToggleAutoAssign={this.toggleAutoAssign}
                    />
                  </div>
                </React.Fragment>
              ))}
              {this.state.currentDraggedIndex >= 0 &&
                (this.state.currentDragOverIndex === this.props.assignments.length - 1 ||
                  this.state.currentDragOverIndex === this.props.assignments.length) && (
                  <div
                    className="dropzone"
                    onDragEnter={(_) => this.onDragEnter(this.props.assignments.length)}
                  ></div>
                )}
            </div>

            <div>
              <SearchFieldSelect
                placeholder={i18n["Form.EditAssignment.Treatments.Search"]}
                onSelect={this.assignTreatment}
                options={this.getUnassignedTreatmentOptions()}
                icon="ic_plus"
              />
            </div>

            <hr className="hr-secondary separator" />

            <div className="h-flex space-between">
              <span>{i18n["Form.EditAssignment.ApplyIfHealthy.Label"]}</span>
              <ToggleButton
                value={this.state.applyIfHealthy}
                onChange={(value) =>
                  this.setState({
                    applyIfHealthy: value,
                    assignments: this.state.assignments.map(
                      (it) => ((it.applyIfHealthy = value), it)
                    ),
                  })
                }
              />
            </div>

            <div className="action-container">
              <AsyncAction
                promise={this.save}
                disabled={false}
                buttonLabel={i18n["Form.EditAssignment.Submit"]}
              />
            </div>
          </div>
        </ModalPopup>
      </div>
    );
  }

  componentDidUpdate() {
    verticalDropdownUtil.reAttach();
  }

  private save(): Promise<any> {
    const autoAssignedTreatmentsId = this.state.assignments
      .filter((it) => it.isAutoAssigned)
      .map((it) => it.treatment.uid);
    const treatmentIds = this.state.assignments.map((it) => {
      return {
        uid: it.treatment.uid,
        position: it.position,
      };
    });
    return sfetch(this.criteriaGroup.apiUrl, {
      method: "PUT",
      body: JSON.stringify({
        treatments: treatmentIds,
        autoAssignedTreatments: autoAssignedTreatmentsId,
        applyIfHealthy: this.state.applyIfHealthy,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => this.props.onChange(this.state.assignments))
      .then(this.props.closeModal);
  }

  private assignTreatment(treatmentUid: string) {
    const treatment = this.props.treatments.find((it) => it.uid === treatmentUid);
    const newTreatmentAssignment: TreatmentAssignment = {
      treatment: treatment,
      criteriaGroup: this.criteriaGroup,
      isAutoAssigned: false,
      applyIfHealthy: this.state.applyIfHealthy,
      position: this.state.assignments.length,
    };
    const assignments = [...this.state.assignments, newTreatmentAssignment];
    this.setState({ assignments, displayLastTreatmentMessage: false });
  }

  private unassignTreatment(treatment: Treatment) {
    if (this.state.assignments.length === 1) {
      this.setState({ displayLastTreatmentMessage: true });
    } else {
      const assignments = this.state.assignments.map((it) => ({ ...it }));
      assignments.splice(
        assignments.findIndex((it) => it.treatment === treatment),
        1
      );
      if (!assignments.find((it) => it.isAutoAssigned)) {
        const nextDefaultTreatment = assignments.sort(byPosition)[0];
        nextDefaultTreatment.isAutoAssigned = true;
        assignments.shift();
        assignments.unshift(nextDefaultTreatment);
      }
      this.setState({ assignments, displayLastTreatmentMessage: false });
    }
  }

  private getUnassignedTreatmentOptions(): Option[] {
    const assignedTreatmentUids = this.state.assignments.map((it) => it.treatment.uid);
    return this.props.treatments
      .filter((it) => !assignedTreatmentUids.includes(it.uid))
      .map((it) => new Option(it.uid, it.name));
  }

  private toggleAutoAssign(assignmentUid: string) {
    const autoAssigned = this.state.assignments.filter((assignment) => assignment.isAutoAssigned);
    if (autoAssigned.length === 1 && autoAssigned[0].treatment.uid === assignmentUid) {
      // Don't remove the last auto assigned
      return;
    }
    const assignments = this.state.assignments.map((assignment) => ({
      ...assignment,
      isAutoAssigned:
        assignment.treatment.uid === assignmentUid
          ? !assignment.isAutoAssigned
          : assignment.isAutoAssigned,
    }));
    this.setState({ assignments });
  }

  private onDragStart(position: number) {
    this.setState({ currentDraggedIndex: position });
  }

  private onDragEnter(position: number) {
    this.setState({ currentDragOverIndex: position });
  }

  private onDrop(e: React.DragEvent, item: any) {
    e.preventDefault();
    if (this.onRowDrop) {
      const startDnDIndex = this.state.currentDraggedIndex;
      const endDnDIndex = this.state.currentDragOverIndex;
      this.onRowDrop(item, startDnDIndex, endDnDIndex);
    }
    this.setState({ currentDraggedIndex: null });
    this.setState({ currentDragOverIndex: null });
  }

  private onRowDrop(item: any, startIndex: number, endIndex: number) {
    if (startIndex !== endIndex) {
      const assignments = this.state.assignments.map((it) => ({
        ...it,
      }));
      const movedItem = assignments.splice(startIndex, 1);
      assignments.splice(endIndex, 0, movedItem[0]);

      for (let p = 0; p < assignments.length; p++) {
        assignments[p].position = p;
      }
      this.setState({ assignments });
    }
  }
}
